import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

const CALENDLY_URL = 'https://paystack.com/buy/private-clarity-call-sdonrr';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Work With Me', href: '/work-with-me' },
  { label: 'Shop', href: '/shop' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLinkClick = () => {
    setMobileOpen(false);
  };

  return (
    <nav
      ref={navRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#FDFAF8]/95 backdrop-blur-md shadow-sm'
          : 'bg-[#FDFAF8]/10 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="font-body text-base md:text-lg tracking-normal text-[#1A1A1A] transition-colors duration-500"
          >
            MANDIE OZEE.
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-body tracking-wide whitespace-nowrap transition-colors duration-300 ${
                  scrolled
                    ? 'text-[#4A4A4A] hover:text-[#1A1A1A]'
                    : 'text-[#333333] hover:text-[#1A1A1A]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center px-5 py-2.5 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap"
          >
            Book a Clarity Call
          </a>
          <p className="hidden md:block text-[#D4006A] text-xs mt-1 text-center">
            <a href="https://paystack.com/buy/private-clarity-call-sdonrr" target="_blank" rel="noopener noreferrer" className="underline">Private Clarity Call</a>
          </p>

          {/* Mobile CTA + Hamburger */}
          <div className="flex items-center gap-3 md:hidden">
            <div className="flex flex-col items-center">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-[#D4006A] text-white text-xs font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap"
              >
                Book a Call
              </a>
              <p className="text-[#D4006A] text-xs mt-1">
                <a href="https://paystack.com/buy/private-clarity-call-sdonrr" target="_blank" rel="noopener noreferrer" className="underline">Private</a>
              </p>
            </div>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-10 h-10 flex items-center justify-center transition-colors duration-300 ${
                scrolled ? 'text-[#1A1A1A]' : 'text-[#333333]'
              }`}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-[#FDFAF8] backdrop-blur-lg transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-[500px] opacity-100 shadow-lg' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={handleLinkClick}
              className="text-sm font-body text-[#4A4A4A] hover:text-[#1A1A1A] transition-colors py-2"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}